import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from "@mui/material";
import BioSpecimenWizard from "./BioSpecimenWizard";
import BackButton from "./BackButton";
import ForwardButton from "./ForwardButton";
import { useWizardContext } from "./WizardContext";
import { useAuth } from "components/AuthProvider";
import { fetchAdminSampleTypeByLetter } from "utils/api";

const SPECIAL_INSTRUCTIONS = [
  "Ensure proper labeling of all samples with Barcode ID and collection date/ThemeProvider",
  "Maintain appropriate storage conditions as required for each sample type.",
  "Transport Samples at required temperature conditions per protocol.",
];

const CollectionDetailsAndAuthorization = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { encounterId, batchId } = useParams();
  const { updateFormData, formData } = useWizardContext();
  const { user } = useAuth();

  const [authSignature, setAuthSignature] = useState(formData?.authSign || "");
  const [sampleTypeDetails, setSampleTypeDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const encounterData = location.state?.encounterData || {};
  const batch = encounterData?.batch || {};

  // Expected full name for authorization (collector's name, not patient's)
  const expectedFullName = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
    : "";

  // Check if signature matches
  const isSignatureValid = authSignature.trim().toLowerCase() === expectedFullName.toLowerCase();

  useEffect(() => {
    const fetchSampleTypeDetails = async () => {
      try {
        setLoading(true);
        const expectedSampleTypes = batch.expectedSampleTypes || [];

        if (expectedSampleTypes.length === 0) {
          setError("No sample types found for this batch.");
          setLoading(false);
          return;
        }

        // Fetch all sample type details in parallel
        const details = await Promise.all(
          expectedSampleTypes.map(letter =>
            fetchAdminSampleTypeByLetter(letter)
          )
        );

        setSampleTypeDetails(
          details.map((sampleType, index) =>
            sampleType || {
              sampleLetter: expectedSampleTypes[index],
              longName: `Sample ${expectedSampleTypes[index]}`,
            }
          )
        );
      } catch (err) {
        console.error("Error fetching sample type details:", err);
        setError("Failed to load sample type details.");
      } finally {
        setLoading(false);
      }
    };

    fetchSampleTypeDetails();
  }, [batch.expectedSampleTypes]);

  const handleBack = () => {
    // Save current signature to context
    updateFormData({ authSign: authSignature });
    navigate(`/biobank/sample-collection/encounters/${encounterId}/batches/${batchId}/review`, {
      state: location.state
    });
  };

  const handleForward = () => {
    if (!isSignatureValid) {
      return; // Button should be disabled, but double-check
    }
    // Save signature to context
    updateFormData({ authSign: authSignature });
    navigate(`/biobank/sample-collection/encounters/${encounterId}/batches/${batchId}/submit`, {
      state: location.state
    });
  };

  return (
    <BioSpecimenWizard>
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h5" gutterBottom fontWeight={600} sx={{ color: "#500000", mb: 1, textAlign: "center" }}>
          Important Collection Details & Authorization
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, color: "text.secondary" }}>
          Please review the following information and type your full name to authorize the collection.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Special Instructions */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: "#500000" }}>
            Special Instructions
          </Typography>
          {SPECIAL_INSTRUCTIONS.map((instruction, index) => (
            <Typography key={index} variant="body1" sx={{ mb: 1 }}>
              {index + 1}. {instruction}
            </Typography>
          ))}
        </Box>

        {/* Sample Collection Details Table */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: "#500000" }}>
            Sample Collection Details
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer sx={{ border: "1px solid #e0e0e0" }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Sample Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Volume (mL)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Collection Type/Container Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Site Collected</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sampleTypeDetails.map((st) => (
                    <TableRow key={st.sampleLetter}>
                      <TableCell>{st.longName || st.shortName || st.sampleLetter}</TableCell>
                      <TableCell>{st.volume || "N/A"}</TableCell>
                      <TableCell>{st.quantity || "N/A"}</TableCell>
                      <TableCell>{st.container || "N/A"}</TableCell>
                      <TableCell>{st.site || "N/A"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
            *Please insert Stable Plasma Protein Note here.
          </Typography>
        </Box>

        {/* Authorization */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: "#500000" }}>
            Authorization:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please type your full name to authorize all information is correct and understood. <span style={{ color: 'red' }}>*</span>
          </Typography>

          <TextField
            fullWidth
            value={authSignature}
            onChange={(e) => setAuthSignature(e.target.value)}
            placeholder="Type your full name here"
            helperText={
              authSignature.trim() === ""
                ? `Expected: ${expectedFullName}`
                : isSignatureValid
                ? "Name verified"
                : "Name does not match"
            }
            error={authSignature.trim() !== "" && !isSignatureValid}
            sx={{ mb: 2 }}
          />
        </Box>
      </Box>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <BackButton onClick={handleBack} />
        <ForwardButton onClick={handleForward} disabled={!isSignatureValid} />
      </Stack>
    </BioSpecimenWizard>
  );
};

export default CollectionDetailsAndAuthorization;
